import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:8001';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 30000,
  // Serializa arrays como params repetidos (?agent=A&agent=B) para que FastAPI
  // los reciba como List[str] en los filtros multiselección (no ?agent[]=A).
  paramsSerializer: { indexes: null },
});

// Attach JWT from localStorage to every request.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('gpvoice_token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-logout on 401 responses.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('gpvoice_token');
      localStorage.removeItem('gpvoice_user');
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

// ---- Auth ------------------------------------------------------------------

export const loginApi = async ({ email, password }) => {
  const res = await api.post('/auth/login', { email, password });
  const { access_token, user } = res.data;
  localStorage.setItem('gpvoice_token', access_token);
  localStorage.setItem('gpvoice_user', JSON.stringify(user));
  return { access_token, user };
};

export const meApi = () => api.get('/auth/me');

export const changePasswordApi = ({ current_password, new_password }) =>
  api.post('/auth/change-password', { current_password, new_password });

// Admin: gestión de usuarios (sin correo — el admin entrega la contraseña).
export const listUsersApi = () => api.get('/auth/users');

export const createUserApi = ({ email, role, agent_name, full_name, password, permitir_sin_llamadas }) =>
  api.post('/auth/users', {
    email,
    role,
    agent_name: agent_name ? agent_name : null,
    full_name: full_name ? full_name : null,
    password: password ? password : null,
    // El backend rechaza un agent_name que no empata con ninguna llamada; esto
    // es la afirmación explícita de que es un agente de nuevo ingreso.
    permitir_sin_llamadas: !!permitir_sin_llamadas,
  });

export const adminResetPasswordApi = (userId, newPassword) =>
  api.post(`/auth/users/${userId}/reset-password`, {
    new_password: newPassword ? newPassword : null,
  });

// Dar de baja / reactivar. El motivo es obligatorio al dar de baja y no se
// pide al reactivar (asimetría deliberada; ver la migración 034).
export const cambiarEstadoUsuarioApi = (userId, { activo, motivo }) =>
  api.patch(`/auth/users/${userId}/estado`, {
    activo: !!activo,
    motivo: motivo ? motivo : null,
  });

// Cambio de rol entre las cuentas dueñas del sistema. `agent_name` es
// obligatorio al bajar a `agent`: sin un nombre que empate con Genesys, esa
// cuenta ve la aplicación completa y la lista de llamadas vacía.
export const cambiarRolUsuarioApi = (userId, { rol, agent_name, permitir_sin_llamadas }) =>
  api.patch(`/auth/users/${userId}/rol`, {
    rol,
    agent_name: agent_name ? agent_name : null,
    permitir_sin_llamadas: !!permitir_sin_llamadas,
  });

// Qué cuelga de la cuenta. Se consulta ANTES de ofrecer el borrado, para decir
// qué lo impide en lugar de un 409 después de apretar el botón.
export const dependenciasUsuarioApi = (userId) =>
  api.get(`/auth/users/${userId}/dependencias`);

// Borrado duro. `data` y no un segundo argumento: axios manda el cuerpo de un
// DELETE sólo por esa vía.
export const borrarUsuarioApi = (userId, motivo) =>
  api.delete(`/auth/users/${userId}`, { data: { motivo } });

export const logoutApi = () => {
  localStorage.removeItem('gpvoice_token');
  localStorage.removeItem('gpvoice_user');
};

export const getStoredUser = () => {
  try {
    const raw = localStorage.getItem('gpvoice_user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// ---- Existing endpoints ----------------------------------------------------

export const getOverview = (params) => api.get('/analytics/overview', { params });
export const getCreceBreakdown = (params = {}) => api.get('/analytics/crece-breakdown', { params });
export const getCalls = (params) => api.get('/calls/', { params });
// CSV de TODO lo filtrado, no sólo la página visible (ver DownloadButton en
// CallsExplorer). `responseType: 'blob'` porque la respuesta ya es el archivo
// armado por el servidor; sin timeout del default (30s) porque el total
// filtrado puede ser de miles de filas y el servidor transmite por lotes.
export const getCallsExport = (params) =>
  api.get('/calls/export', { params, responseType: 'blob', timeout: 0 });
export const getCallDetail = (id) => api.get(`/calls/${id}`);
/**
 * Corrige a mano si una llamada es aprovechable. Sólo administradores — el
 * backend lo exige con `require_admin`, esto no es la única barrera.
 * `esAprovechable === null` quita la corrección y vuelve al criterio automático.
 */
export const corregirAprovechamiento = (id, esAprovechable) =>
  api.patch(`/calls/${id}/aprovechable`, { es_aprovechable: esAprovechable });
// El rango viaja porque las opciones de CONCLUSIÓN dependen de él: los dos
// catálogos de tipificación tienen una frontera de fecha (19-ago-2026), y
// ofrecer el que no cae en el rango son once o veinticuatro opciones que sólo
// pueden devolver cero. Quién cabe en qué rango lo decide el backend
// (`constants.catalogos_del_rango`): duplicar esa frontera en JavaScript es
// garantizar que un día las dos copias digan cosas distintas.
export const getFilters = (params = {}) => api.get('/calls/filters', { params });
// Descartar la penalización por error de una boleta, o deshacerlo.
//   esError = false → no fue un error; la nota se recalcula sin la penalización
//   esError = null  → volver al veredicto automático
// `true` lo rechaza el backend a propósito: penalizar a mano no está
// contemplado (ver `CorreccionErrorBoleta`).
export const descartarErrorBoleta = (callId, esError, nota) =>
  api.patch(`/calls/${callId}/boleta/error`, { es_error: esError, nota: nota || null });
export const getAgentStats = (params) => api.get('/agents/', { params });
export const getQueueStats = (params) => api.get('/analytics/queues', { params });
export const getProcessingStatus = () => api.get('/analytics/processing-status');

// Temporal analytics
export const getHeatmapData = (params) => api.get('/analytics/temporal/heatmap', { params });
export const getHourlyTrend = (params) => api.get('/analytics/temporal/hourly-trend', { params });
export const getPeakHours = (params) => api.get('/analytics/temporal/peak-hours', { params });

// Duration analytics
export const getDurationDistribution = (params) => api.get('/analytics/duration/distribution', { params });
export const getDurationComparison = (params) => api.get('/analytics/duration/comparison', { params });

// Conversation analytics
export const getConversationAnalytics = (params) => api.get('/analytics/conversation/', { params });
export const getConversationByAgent = (params) => api.get('/analytics/conversation/by-agent', { params });

// Detail drill-downs
export const getAgentDetail = (name, params) => api.get(`/agents/${encodeURIComponent(name)}`, { params });
export const getQueueDetail = (name, params) => api.get(`/analytics/queues/${encodeURIComponent(name)}`, { params });

// Forecast
export const getForecast = () => api.get('/analytics/forecast/');

// LLM Analysis
export const getAnalysisOverview = (params) => api.get('/analytics/analysis/overview', { params });
export const getQualityDistribution = (params) => api.get('/analytics/analysis/quality-distribution', { params });
export const getAnalysisCategories = (params) => api.get('/analytics/analysis/categories', { params });
export const getAnalysisHotels = (params) => api.get('/analytics/analysis/hotels', { params });
export const getContactCenters = (params) => api.get('/analytics/analysis/contact-centers', { params });
export const getSpamBreakdown = (params) => api.get('/analytics/analysis/spam-breakdown', { params });
export const getComplaintsBreakdown = (params = {}) => api.get('/analytics/analysis/complaints-breakdown', { params });
export const getComplaintsTargetBreakdown = (params = {}) => api.get('/analytics/analysis/complaints-target-breakdown', { params });
export const getTariffTypes = (params) => api.get('/analytics/analysis/tariff-types', { params });
export const getQualityByAgent = (params) => api.get('/analytics/analysis/quality-by-agent', { params });
export const getAnalysisProcessingStatus = () => api.get('/analytics/analysis/processing-status');
export const getComplianceByAgent = (params = {}) => api.get('/analytics/analysis/compliance-by-agent', { params });
// La misma boleta a través del tiempo. `bucket` = 'day' | 'week' | 'month'.
export const getComplianceTimeseries = (params = {}) =>
  api.get('/analytics/analysis/compliance-timeseries', { params });
// La réplica del Agente a su calificación (#99). PUT y no POST: la postura
// vigente de una llamada es una sola y volver a mandarla la sustituye.
// Sólo el agente dueño de la llamada puede escribirla; el supervisor la lee
// en el detalle. El backend responde 403 a cualquier otro caso.
// La réplica es POSTURA + TEXTO. La lista de criterios señalados se retiró el
// 31-ago-2026 (migración 026): convertía el argumento del agente en una
// etiqueta, y lo que el supervisor necesita leer es el argumento.
export const guardarReplica = (callId, { deAcuerdo, motivo = null }) =>
  api.put(`/calls/${callId}/replica`, {
    de_acuerdo: deAcuerdo,
    motivo: motivo || null,
  });
// El supervisor responde por escrito a la réplica. SOLO AGREGA: no hay endpoint
// para editar ni borrar, y es deliberado — lo que se puede reescribir después no
// sirve como rastro de qué se decidió. El agente los ve.
export const comentarReplica = (callId, texto) =>
  api.post(`/calls/${callId}/replica/comentario`, { texto });
// El desenlace de la réplica: 'aceptada' | 'rechazada' | null (vuelve a
// pendiente). Registra el JUICIO, no la corrección — aceptar NO mueve la
// calificación, y la pantalla tiene que decirlo o se lee como burla.
// Corrige el veredicto de UN criterio y recalcula la nota. `veredicto: null`
// quita la corrección. El motivo es obligatorio al bajar a 'no': quitarle puntos
// a alguien por juicio propio tiene que quedar argumentado.
export const corregirCriterio = (callId, clave, veredicto, motivo = null) =>
  api.patch(`/calls/${callId}/boleta/criterio`, { clave, veredicto, motivo });
export const resolverReplica = (callId, resolucion) =>
  api.patch(`/calls/${callId}/replica/resolucion`, { resolucion });
export const getTypificationByAgent = (params = {}) => api.get('/analytics/analysis/typification-by-agent', { params });
// `catalog` va SIN default: `null` significa "el que le toque a esta llamada",
// y lo deriva el servidor de su fecha. El default `'v1'` que había aquí era una
// afirmación equivocada disfrazada de omisión — desde el 19-ago-2026 el
// catálogo vigente es el v2, así que toda corrección se validaba contra los
// once códigos `CR-*` y rebotaba con 422.
export const setTypificationOverride = (callId, manualCode, note, catalog = null) =>
  api.post(`/calls/${callId}/typification-override`, {
    manual_code: manualCode,
    catalog,
    note: note || null,
  });
export const getConversion = (params) => api.get('/analytics/analysis/conversion', { params });
// La misma tasa por periodo (día/semana/mes), total o desglosada por agente/hotel/cola.
export const getConversionTimeseries = (params) => api.get('/analytics/analysis/conversion-timeseries', { params });
export const getUsefulness = (params = {}) => api.get('/analytics/analysis/usefulness', { params });
export const getSalesReport = (params = {}) => api.get('/analytics/analysis/sales-report', { params });
export const getAgentBenchmark = (params = {}) => api.get('/analytics/analysis/agent-benchmark', { params });

// Customers (Req #8 — perfilamiento)
export const listCustomers = (params) => api.get('/customers/', { params });
export const getCustomerDetail = (id) => api.get(`/customers/${id}`);

// Chat
export const chatWithCall = (callId, question, history) =>
  api.post(`/calls/${callId}/chat`, { question, history }, { timeout: 60000 });

// Library chat (Req #13 — chat sobre toda la biblioteca)
export const chatWithLibrary = (question, history) =>
  api.post('/library/chat', { question, history }, { timeout: 90000 });

export default api;

// ─── Coaching (registro de retroalimentación, issue #103) ───────────────────
export const getAgentesCoaching = () => api.get('/coaching/agentes');
// El panorama del día: una fila por agente. Es lo primero que ve el supervisor.
export const getResumenDelDiaCoaching = (fecha) =>
  api.get('/coaching/resumen-del-dia', { params: { fecha } });
// Las llamadas del agente en el lapso, para elegir de una lista en vez de
// teclear ids. `hasta` es opcional del lado del API: sin él el lapso es de un
// solo día, que es como nació la pantalla y sigue siendo el caso común.
//
// `tipificacion` viaja como arreglo y axios lo serializa repitiendo la clave
// (`?tipificacion=a&tipificacion=b`), que es justo lo que FastAPI espera en un
// `Query(list[str])`. No hay que unirlas con comas: el catálogo de CRECE trae
// etiquetas con comas dentro.
export const getLlamadasCoaching = (agenteId, desde, hasta, opciones = {}) =>
  api.get('/coaching/llamadas', {
    params: {
      agente_id: agenteId, desde, hasta,
      ...(opciones.orden ? { orden: opciones.orden } : {}),
      ...(opciones.tipificacion?.length ? { tipificacion: opciones.tipificacion } : {}),
      ...(opciones.categoria?.length ? { categoria: opciones.categoria } : {}),
      ...(opciones.senal?.length ? { senal: opciones.senal } : {}),
      ...(opciones.tipificacionEstado ? { tipificacion_estado: opciones.tipificacionEstado } : {}),
    },
  });
export const getSesionesCoaching = (params = {}) => api.get('/coaching/sesiones', { params });
export const registrarSesionCoaching = (payload) => api.post('/coaching/sesiones', payload);
// El acuse es la firma del agente: POST una sola vez, el backend responde 409
// si ya existe — no hay edición.
export const acusarSesionCoaching = (sesionId, comentario) =>
  api.post(`/coaching/sesiones/${sesionId}/acuse`, { comentario: comentario || null });

// Conocimiento de hoteles (#109). Detrás de la bandera `conocimiento_en_vivo`:
// con la bandera apagada estos endpoints responden 403 a todo el mundo.
export const getHotelesConocimiento = () => api.get('/conocimiento/hoteles');
export const getFichaHotel = (documentoId) => api.get(`/conocimiento/hotel/${documentoId}`);
export const buscarConocimiento = (q, hotel) =>
  api.get('/conocimiento/buscar', { params: hotel ? { q, hotel } : { q } });

// Cobertura de coaching a día vencido (#105). Sólo admin.
// Las banderas EFECTIVAS de un agente, para "Ver como agente". Admin-only.
// El menú de la previsualización tiene que pintar lo que ESE agente ve, no lo
// que ve el supervisor que está mirando.
export const getBanderasDeAgente = (agentName) =>
  api.get('/banderas/de-agente', { params: { agent_name: agentName } });

// La pantalla de Inicio del ejecutivo, en una sola llamada. El agente recibe lo
// suyo (el servidor toma su nombre del token); el admin pide el de un agente
// con `agentName`, que es lo que usa "Ver como agente".
// El Overview del supervisor: el equipo con las mismas definiciones del Inicio.
// Los filtros del Inicio, como params. Una sola copia para el Inicio y el
// Overview: los dos endpoints los validan con el mismo `_filtros`.
const paramsDeFiltrosInicio = (filtros = {}) => ({
  ...(filtros.tipo?.length ? { tipo: filtros.tipo } : {}),
  ...(filtros.utilidad ? { utilidad: filtros.utilidad } : {}),
  ...(filtros.venta?.length ? { venta: filtros.venta } : {}),
  ...(filtros.categoria?.length ? { categoria: filtros.categoria } : {}),
  ...(filtros.aprovechamiento ? { aprovechamiento: filtros.aprovechamiento } : {}),
});

// `excluidos`: el «todos menos Tanya» de `FiltroDeAgentes`. Lo manda quien
// llama (y no se importa aquí) porque ese módulo ya importa de éste.
export const getInicioEquipo = (filtros = {}, periodo = 'dia', excluidos = []) =>
  api.get('/inicio/equipo', {
    params: {
      ...paramsDeFiltrosInicio(filtros),
      periodo,
      ...(excluidos.length ? { agent_excluir: excluidos } : {}),
    },
  });

export const getInicio = (agentName, filtros = {}) =>
  api.get('/inicio', {
    params: {
      ...(agentName ? { agent_name: agentName } : {}),
      ...paramsDeFiltrosInicio(filtros),
    },
  });

export const getCoberturaCoaching = (params = {}) =>
  api.get('/coaching/cobertura', { params });
